// src/utils/api.js
import axios from "axios";
import { getAuth, onAuthStateChanged } from "firebase/auth";

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:5001/api";

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  // withCredentials not needed unless you are using cookie sessions
});

/** ---------- Auth readiness gate ----------
 * Ensure we don't fire requests before Firebase Auth has emitted its initial state.
 */
let authInitPromise;
function waitForAuthInit() {
  if (authInitPromise) return authInitPromise;
  const auth = getAuth();
  authInitPromise = new Promise((resolve) => {
    const unsub = onAuthStateChanged(auth, () => {
      unsub();
      resolve();
    });
  });
  return authInitPromise;
}

/** Get a (possibly fresh) token if signed in */
async function getToken(force = false) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken(force);
  } catch {
    return null;
  }
}

/** ---------- Request interceptor ----------
 * Wait for auth init, then attach Authorization if we have a token.
 * Also set Content-Type smartly.
 */
api.interceptors.request.use(
  async (config) => {
    await waitForAuthInit();

    // Attach token
    const token = await getToken(false);
    config.headers = config.headers || {};

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
        // Only warn if we are on a protected route. 
        // For now, let's just log it to be sure.
        if (config.url?.startsWith('/')) {
             console.warn("API Request without token:", config.url);
        }
      delete config.headers.Authorization;
    }

    // Content-Type handling
    const isFormData =
      typeof FormData !== "undefined" && config.data instanceof FormData;

    if (isFormData) {
      delete config.headers["Content-Type"];
      delete config.headers["content-type"];
    } else {
      const method = (config.method || "get").toLowerCase();
      const mayHaveBody = ["post", "put", "patch"].includes(method);
      const hasCT =
        "Content-Type" in config.headers || "content-type" in config.headers;

      if (
        mayHaveBody &&
        config.data &&
        typeof config.data === "object" &&
        !hasCT
      ) {
        config.headers["Content-Type"] = "application/json";
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/** ---------- Response interceptor ----------
 * If we get a 401 once, force-refresh the token and retry the request one time.
 */
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;

    // Only retry once per request
    if (status === 401 && original && !original.__retried401) {
      original.__retried401 = true;

      // Force refresh the token
      const fresh = await getToken(true);
      if (fresh) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${fresh}`;
        return api(original);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
